"use strict";

Object.defineProperty(exports, "__esModule", {
    value: true
});
exports.SEASONS = undefined;
exports.deriveSeason = deriveSeason;
exports.getAstronomicalSeason = getAstronomicalSeason;
exports.getMeteorologicalSeason = getMeteorologicalSeason;
exports.parseSeason = parseSeason;

var _moonbeams = require("moonbeams");

var _moonbeams2 = _interopRequireDefault(_moonbeams);

var _uiwidgetsCommon = require("uiwidgets-common");

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

/**
 * Get a JavaScript Data object for the given season index and year. The season indices are:
 *
 * 0 - March Equinox
 * 1 - June Solstice
 * 2 - September Equinox
 * 3 - December Solstice
 */
function seasonalDate(seasonIdx, year) {
    var jd = _moonbeams2.default.season(seasonIdx, year);
    var start = _moonbeams2.default.jdToCalendar(jd);
    // NOTE: really important to use Math.floor on the day here, Node/Browsers behave VERY differently when
    // passed a non-integer day.
    return new Date(Date.UTC(start.year, start.month - 1, Math.floor(start.day)));
}

/**
 * A hash of the seasons, keyed by language. Each property in this object should be a BCP-47 language key. The key
 * should be used as a lookup for the language specific strings for "SPRING", "SUMMER", "AUTUMN" and "WINTER". Where
 * there may be a number of identifiers (e.g. "Autumn" and "Fall"), an array can be provided for the key, with the
 * first item in the array being the preferred one and the one used for formatting.
 *
 * @name SEASONS
 * @type {Object} a hash of BCP-47 language codes, each containing the localised strings for the seasons.
 */
var SEASONS = exports.SEASONS = {
    "en": {
        SPRING: "Spring",
        SUMMER: "Summer",
        AUTUMN: ["Fall", "Autumn"],
        WINTER: "Winter"
    }
};

/**
 * Derive a season from a Date or fuzzy date object.
 *
 * @param value {Date|Object} the Date object or an object representing a fuzzy date.
 * @param [meteorological] {Boolean} true to use meteorological seasons, false for astronomical.
 * @returns {String} the season key.
 */
function deriveSeason(value, meteorological) {
    var seasonFn = meteorological ? getMeteorologicalSeason : getAstronomicalSeason,
        f,
        date,
        seasonKey,
        seasonKeys,
        season,
        seasons;

    if (!value) {
        return;
    }

    if ((0, _uiwidgetsCommon.isDate)(value)) {
        date = value;
    } else if (value.start) {
        if (!(0, _uiwidgetsCommon.isDate)(value.start)) {
            date = new Date(value.start);
        } else {
            date = value.start;
        }
    } else {
        return;
    }

    seasons = seasonFn(undefined, date.getFullYear());
    seasonKeys = Object.keys(seasons);

    for (f = 0; f < seasonKeys.length; f++) {
        seasonKey = seasonKeys[f];
        season = seasons[seasonKey];
        if (date >= season.start && date < season.end) {
            return seasonKeys[f];
        }
    }

    if (date < seasons["SPRING"].start) {
        return "WINTER";
    }
}

/**
 * Get the start/end dates for astronomical season[s].
 *
 * @param seasonKey {String|Array|undefined}
 * @param year {Number} the year.
 * @returns {Object} an object with "start" and "end" properties, defining the start and end dates of the season.
 */
function getAstronomicalSeason(seasonKey, year) {
    // if no season key[s] were provided, recall, passing an array of all the seasons.
    if (!seasonKey) {
        return getAstronomicalSeason(["WINTER", "AUTUMN", "SUMMER", "SPRING"], year);
    }

    // if an array of seasons were provided, build a hash of season keys, where each value is the start/end date of
    // the season.
    if (Array.isArray(seasonKey)) {
        return seasonKey.reduce(function (value, seasonKey) {
            value[seasonKey] = getAstronomicalSeason(seasonKey, year);
            return value;
        }, {});
    }

    seasonKey = seasonKey.toUpperCase();

    if (seasonKey === "WINTER") {
        return {
            start: seasonalDate(3, year),
            end: seasonalDate(0, year + 1)
        };
    } else if (seasonKey === "AUTUMN") {
        return {
            start: seasonalDate(2, year),
            end: seasonalDate(3, year)
        };
    } else if (seasonKey === "SUMMER") {
        return {
            start: seasonalDate(1, year),
            end: seasonalDate(2, year)
        };
    } else if (seasonKey === "SPRING") {
        return {
            start: seasonalDate(0, year),
            end: seasonalDate(1, year)
        };
    }
}

/**
 * Get the start/end dates for meteorological season[s].
 *
 * @param seasonKey {String|Array|undefined}
 * @param year {Number} the year.
 * @returns {Object} an object with "start" and "end" properties, defining the start and end dates of the season.
 */
function getMeteorologicalSeason(seasonKey, year) {
    // if no season key[s] were provided, recall, passing an array of all the seasons.
    if (!seasonKey) {
        return getMeteorologicalSeason(["WINTER", "AUTUMN", "SUMMER", "SPRING"], year);
    }

    // if an array of seasons were provided, build a hash of season keys, where each value is the start/end date of
    // the season.
    if (Array.isArray(seasonKey)) {
        return seasonKey.reduce(function (value, seasonKey) {
            value[seasonKey] = getMeteorologicalSeason(seasonKey, year);
            return value;
        }.bind(this), {});
    }

    seasonKey = seasonKey.toUpperCase();

    if (seasonKey === "WINTER") {
        return {
            start: new Date(Date.UTC(year, 11, 1)),
            end: new Date(Date.UTC(year + 1, 2, 0)) // use march 0, to get the end of february.
        };
    } else if (seasonKey === "AUTUMN") {
        return {
            start: new Date(Date.UTC(year, 8, 1)),
            end: new Date(Date.UTC(year, 10, 30))
        };
    } else if (seasonKey === "SUMMER") {
        return {
            start: new Date(Date.UTC(year, 5, 1)),
            end: new Date(Date.UTC(year, 7, 31))
        };
    } else if (seasonKey === "SPRING") {
        return {
            start: new Date(Date.UTC(year, 2, 1)),
            end: new Date(Date.UTC(year, 4, 31))
        };
    }
}

/**
 * Parse the season from the given string.
 *
 * @param s {String} the string to parse.
 * @returns {String} the season key for the given string or null.
 */
function parseSeason(s) {
    var lang = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : "en";

    var seasons = SEASONS[lang],
        seasonKeys = Object.keys(seasons),
        f,
        key,
        l,
        label,
        labels;

    // iterate through the seasons looking for a match.
    for (f = 0, l = seasonKeys.length; f < l; f++) {
        key = seasonKeys[f];

        // a season may have many labels, e.g. "Fall" or "Autumn", so find if ANY of them match.
        labels = Array.isArray(seasons[key]) ? seasons[key] : [seasons[key]];
        label = labels.find(function (label) {
            return label.toLowerCase() === s;
        });

        // if we find a matching label, we've found our key.
        if (label) {
            return key;
        }
    }

    return null;
}
//# sourceMappingURL=Seasons.js.map